/**
 * Known point maps (GET /v1/things/point-maps): which of a driver's maps fit
 * the device on the Points step. A map lists the product names it was built
 * from; a scan reports the device's own (`identity.product`, e.g.
 * "1769-L36ERM/A LOGIX5336ERM"). A match names the hardware, not the program
 * in it, so it is pointed out and preselected, never loaded by itself. Pure,
 * so it is unit-tested.
 */

import type { PointMapSummary } from "@/lib/api/things";

function norm(s: string): string {
  return s.toLowerCase().replace(/\s+/g, " ").trim();
}

/** Whether one of the map's products is part of the device's reported product name. */
export function mapMatchesProduct(map: Pick<PointMapSummary, "products">, product: string | undefined): boolean {
  const p = norm(product ?? "");
  if (!p) return false;
  return map.products.some((x) => {
    const want = norm(x);
    return want !== "" && p.includes(want);
  });
}

/** The maps with the ones matching the device first (each group keeps the server's order). */
export function orderForDevice<T extends Pick<PointMapSummary, "products">>(maps: readonly T[], product: string | undefined): T[] {
  const hit = maps.filter((m) => mapMatchesProduct(m, product));
  return [...hit, ...maps.filter((m) => !hit.includes(m))];
}

/** The map to preselect: the only one matching the device, else none. */
export function preselectedMap<T extends Pick<PointMapSummary, "id" | "products">>(
  maps: readonly T[],
  product: string | undefined,
): string {
  const hit = maps.filter((m) => mapMatchesProduct(m, product));
  return hit.length === 1 ? hit[0].id : "";
}
